/**
 * Render one connector feed's transfer summary and shared controls.
 *
 * Connector cards report transfer state only. Users start analysis from the
 * application dashboard, using the same controls as uploaded documents.
 */
import {
  Check,
  ChevronDown,
  Folder,
  Loader2,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react'

import { useState } from 'react'
import { connectorScheduleLabels } from '@/subsystems/vault/connectors/components/ConnectorScheduleSelect'
import { connectorDefinition } from '@/subsystems/vault/connectors/registry'
import type { ConnectorAnalysisState, ConnectorAnalysisStatus, ConnectorPendingAction } from '@/subsystems/vault/connectors/types'

import { FeedAnalysisActions } from './FeedAnalysisActions'

type CardState = 'empty' | 'syncing' | 'disconnecting' | 'complete' | 'error'

const badgeStyle: Record<CardState, string> = {
  empty: 'border-slate-200 bg-slate-50 text-slate-500',
  syncing: 'border-blue-200 bg-blue-50 text-blue-700',
  disconnecting: 'border-slate-200 bg-slate-100 text-slate-600',
  complete: 'border-green-200 bg-green-50 text-green-700',
  error: 'border-red-200 bg-red-50 text-red-700',
}

const stateLabel: Record<CardState, string> = {
  empty: 'Nothing synced yet',
  syncing: 'Syncing…',
  disconnecting: 'Disconnecting',
  complete: 'Synced',
  error: 'Sync failed',
}

const pendingBadge: Record<ConnectorPendingAction, string> = {
  pull: 'Syncing records…',
  remove: 'Unsyncing record…',
  sync: 'Syncing…',
  reset: 'Resetting workspace…',
}

function compactDate(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function AnalysisStateBadge({
  state,
  failed,
  pendingAction,
  hasSyncedRecords,
  testId,
}: {
  state: ConnectorAnalysisState
  failed: boolean
  pendingAction?: ConnectorPendingAction
  hasSyncedRecords: boolean
  testId: string
}) {
  const displayedState: CardState = pendingAction
    ? 'syncing'
    : failed
      ? 'error'
      : state === 'disconnecting'
        ? 'disconnecting'
        : state === 'syncing'
          ? 'syncing'
          : hasSyncedRecords
            ? 'complete'
            : 'empty'
  const icon = pendingAction
    ? <Loader2 className="h-3 w-3 animate-spin" />
    : displayedState === 'complete'
      ? <Check className="h-3 w-3" />
      : displayedState === 'error'
        ? <TriangleAlert className="h-3 w-3" />
        : displayedState === 'syncing'
          ? (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-current opacity-40 motion-safe:animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
              </span>
            )
          : null
  const className = `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11.5px] font-semibold leading-none ${badgeStyle[displayedState]}`
  const label = pendingAction ? pendingBadge[pendingAction] : stateLabel[displayedState]
  return <span className={className} data-testid={testId} role={pendingAction ? 'status' : undefined}>{icon}{label}</span>
}

export function FeedAnalysisCard({
  analysis,
  canConfigure,
  canAdministerWorkspace,
  lockedNotice,
  pendingAction,
  onReset,
  onConfigure,
  onDisconnect,
  onPullRecords,
  onRefreshSyncedRecords,
  onSync,
}: {
  analysis: ConnectorAnalysisStatus
  canConfigure: boolean
  canAdministerWorkspace: boolean
  // Shown in place of actions when the viewer cannot configure this
  // connector, so a locked card explains itself instead of looking broken.
  lockedNotice?: string
  pendingAction?: ConnectorPendingAction
  onReset?: () => void
  onConfigure?: () => void
  onDisconnect?: () => void
  onPullRecords?: () => void
  onRefreshSyncedRecords?: () => void
  onSync?: () => void
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const provider = connectorDefinition(analysis.provider)
  const schedule = analysis.schedule === 'manual' ? 'Manual schedule' : connectorScheduleLabels[analysis.schedule]
  const hasSyncedRecords = analysis.sourceRecords > 0
  const showProvider = analysis.sourceName !== provider.label
  const sourceRecordLabel = analysis.sourceRecords === 1
    ? analysis.sourceRecordUnit.singular
    : analysis.sourceRecordUnit.plural

  return (
    <section
      aria-label={`${analysis.sourceName} connector`}
      data-testid={`connector-analysis-feed-${analysis.feedId}`}
      className={`flex min-w-0 flex-col gap-3 rounded-[11px] border p-[18px] ${analysis.lastSyncFailure ? 'border-red-200 bg-red-50/20' : 'border-slate-200 bg-white'}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="min-w-0">
          <h3 className="truncate text-[14.5px] font-[650] text-slate-900">{analysis.sourceName}</h3>
          {showProvider && <p className="text-xs text-slate-400">{provider.label}</p>}
        </div>
        <span className="ml-auto shrink-0">
          <AnalysisStateBadge
            state={analysis.state}
            failed={Boolean(analysis.lastSyncFailure)}
            pendingAction={pendingAction}
            hasSyncedRecords={hasSyncedRecords}
            testId={`connector-analysis-state-${analysis.feedId}`}
          />
        </span>
      </div>

      <div className="text-[12.5px] text-slate-500" data-testid={`connector-progress-${analysis.feedId}`}>
        {hasSyncedRecords
          ? `${analysis.sourceRecords.toLocaleString()} ${sourceRecordLabel} synced · ready to run on the dashboard`
          : analysis.lastSyncFailure
            ? 'Sync did not complete'
            : 'Nothing synced yet'}
      </div>

      {analysis.lastSyncFailure && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800"
          data-testid={`connector-sync-error-${analysis.feedId}`}
          role="alert"
        >
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <div className="min-w-0">
            <strong className="font-semibold">The last sync failed.</strong>{' '}
            <span>{analysis.lastSyncFailure.message}</span>
            <p className="mt-0.5 text-[11px] text-red-600">
              {compactDate(analysis.lastSyncFailure.failedAt)} · Job {analysis.lastSyncFailure.jobId}
            </p>
          </div>
        </div>
      )}

      <button
        type="button"
        data-testid={`connector-details-${analysis.feedId}`}
        className="group border-t border-dashed border-[#eef1f5] pt-2 text-left text-[11px] font-bold tracking-[0.05em] text-slate-400 outline-none hover:text-slate-500"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen(open => !open)}
      >
        <span className="inline-flex items-center gap-1.5 rounded-sm group-focus-visible:ring-2 group-focus-visible:ring-slate-300 group-focus-visible:ring-offset-2">
          <ChevronDown className={`size-[11px] transition-transform duration-150 motion-reduce:transition-none ${detailsOpen ? '' : '-rotate-90'}`} /> DETAILS
        </span>
      </button>
      {detailsOpen && (
        <div className="space-y-[7px] text-xs text-slate-500">
          <p>
            {analysis.connectorDocuments.toLocaleString()} files · {analysis.syncRunCount.toLocaleString()} pull{analysis.syncRunCount === 1 ? '' : 's'}
            {analysis.syncRuns[0] ? ` · last pull ${compactDate(analysis.syncRuns[0].completedAt)}` : ''} · {schedule}
          </p>
          {analysis.syncRuns.length > 0 && (
            <div className="space-y-[3px]">
              <p className="text-[10.5px] font-bold tracking-[0.05em] text-slate-400">PULL HISTORY</p>
              {analysis.syncRuns.map(run => (
                <p key={run.jobId} className="text-[11.5px]">
                  {run.sourceRecords.toLocaleString()} {run.sourceRecords === 1 ? analysis.sourceRecordUnit.singular : analysis.sourceRecordUnit.plural} · {compactDate(run.completedAt)} ·{' '}
                  <span className="font-semibold text-slate-600">{run.documentCount.toLocaleString()} files</span>
                </p>
              ))}
            </div>
          )}
          <p className="flex items-center gap-1.5 text-slate-400">
            <Folder className="h-3 w-3" />
            Delivers to {analysis.destinationVaultName} / {analysis.destinationFolderName}
          </p>
          <p className="text-slate-400">
            Configured: <strong className="font-semibold text-slate-600">{analysis.configurationSummary[0]}</strong>
            {analysis.configurationSummary.slice(1).map(segment => <span key={segment}> · {segment}</span>)}
          </p>
        </div>
      )}
      {!canConfigure && lockedNotice && analysis.state !== 'disconnecting' && (
        <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
          {lockedNotice}
        </p>
      )}

      {(canConfigure || canAdministerWorkspace) && analysis.state !== 'disconnecting' && (
        <FeedAnalysisActions
          feedId={analysis.feedId}
          sourceName={analysis.sourceName}
          managedRecordUnit={analysis.provider === 'kuali-research'
            ? { singular: 'award', plural: 'awards' }
            : { singular: 'record', plural: 'records' }}
          pendingAction={pendingAction}
          onReset={onReset}
          onConfigure={onConfigure}
          onDisconnect={onDisconnect}
          onPullRecords={onPullRecords}
          onRefreshSyncedRecords={onRefreshSyncedRecords}
          onSync={onSync}
        />
      )}
    </section>
  )
}
